import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { LocalStorageService } from 'src/app/local-storage.service';
import { Page } from 'src/app/page';
import { resetFilterByDateUtil } from 'src/app/utils/filterListByDate';
import { Activity } from 'src/models/activity.model';
import { CustomPaginationService } from 'src/services/custom-pagination.service';
import { DataSharingService } from 'src/services/data-sharing.service';
import { TrackingListService } from 'src/services/tracking-list.service';
import { ModalSizeEnum } from 'src/types';
import { DeleteTrackingComponent } from '../delete-tracking/delete-tracking.component';
import { ServiceProviderManageTrackingComponent } from '../service-provider-manage-tracking/service-provider-manage-tracking.component';
import { ServiceProvidersService } from 'src/services/service-providers.service';

@Component({
  selector: 'service-provider-subject-tracking',
  templateUrl: './service-provider-subject-tracking.component.html',
  styleUrls: ['./service-provider-subject-tracking.component.scss']
})
export class ServiceProviderSubjectTrackingComponent implements OnInit {
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  filterForm: FormGroup;
  filterValue: {
    minDate: any;
    maxDate: any;
    providedServices: any
  };
  appliedFilters: {
    displayValue: string;
    value: Object | string;
    key: 'minDate' | 'maxDate' | 'providedServices';
  }[] = [];
  badgeCount: number;
  object: any = {
    trackingData: '',
    action: '',
    subjectId: '',
  };
  subjectId: number;
  isLoading: boolean = true;
  page: Page<Activity> = new Page();
  addButtonDisabled = true;
  loggedIn: any;
  maxDate: Date = new Date(2999, 11, 31);
  serviceProviderId;
  providedServices = [];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private paginationService: CustomPaginationService,
    private trackingListService: TrackingListService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private dataSharingService: DataSharingService,
    private localStorageService: LocalStorageService,
    private serviceProvidersService: ServiceProvidersService
  ) { 
    this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
    if (this.filterValue === undefined) {
      this.filterValue = {
        minDate: '',
        maxDate: '',
        providedServices: []
      };
    }

    this.dataSharingService.subjectStatusChange.subscribe(result => {
      if (result !== null) {
        this.addButtonDisabled = result;
      }
    });

    this.serviceProviderId = this.loggedIn?.serviceProviderId;

    this.serviceProvidersService.findProvidedServicesByServiceProviderId(this.serviceProviderId).subscribe(res => {
      this.providedServices = res;
    });
  }

  ngOnInit(): void {
    this.filterForm = this.formBuilder.group({
      minDate: ['', []],
      maxDate: ['', []],
      providedServices: [null]
    });
    this.getTrackingData();
  }

  public getTrackingData() {
    const url = this.router.url;
    if (url.includes('/subjects/service-providers/')) {
      this.addButtonDisabled = false;
      const [, id] = url.split('/subjects/service-providers/');
      this.subjectId = Number(id);
      this.getDataForSubject(this.filterValue);
    }
  }

  public getNextPage(): void {
    this.isLoading = true;
    this.page.pageable = this.paginationService.getNextPage(this.page);
    this.getData(this.filterValue);
  }

  public getPreviousPage(): void {
    this.isLoading = true;
    this.page.pageable = this.paginationService.getPreviousPage(this.page);
    this.getData(this.filterValue);
  }

  public getFirstPage(): void {
    this.isLoading = true;
    this.page.pageable = this.paginationService.getFirstPage(this.page);
    this.getData(this.filterValue);
  }

  public getLastPage(): void {
    this.isLoading = true;
    this.page.pageable = this.paginationService.getLastPage(this.page);
    this.getData(this.filterValue);
  }

  public getPageInNewSize(pageSize: any): void {
    this.isLoading = true;
    this.page.pageable = this.paginationService.getPageInNewSize(this.page, pageSize);
    this.getData(this.filterValue);
  }

  getDataForSubject(filter: any) {
    this.trackingListService.getForServiceProviderSubject(this.page.pageable, filter, this.subjectId, this.serviceProviderId).subscribe(page => {
      this.page = page;
      this.page.pageable.pagePerShow = page.number + 1;
      this.isLoading = false;
      if (!this.cdr['destroyed']) {
        this.cdr.detectChanges();
      }
    });
  }

  applyFilter() {
    this.isLoading = true;
  
    this.filterValue.minDate = this.filterForm.get('minDate').value ? this.datePipe.transform(new Date(this.filterForm.get('minDate').value), 'yyyy-MM-dd') : '';
    this.filterValue.maxDate = this.filterForm.get('maxDate').value ? this.datePipe.transform(new Date(this.filterForm.get('maxDate').value), 'yyyy-MM-dd') : '';
    this.filterValue.providedServices = this.filterForm.get('providedServices').value ?? [];
  
    this.appliedFilters = [];
  
    if (this.filterValue.minDate) {
      this.appliedFilters.push({ key: 'minDate', value: this.filterValue.minDate, displayValue: this.filterValue.minDate });
    }
    if (this.filterValue.maxDate) {
      this.appliedFilters.push({ key: 'maxDate', value: this.filterValue.maxDate, displayValue: this.filterValue.maxDate });
    }
  
    if (this.filterValue.providedServices.length) {
      this.filterValue.providedServices.forEach((id: number) => {
        const service = this.providedServices.find(ps => ps.id === id);
        if (service) {
          this.appliedFilters.push({ key: 'providedServices', value: id, displayValue: service.title });
        }
      });
    }
  
    this.badgeCount = this.appliedFilters.length;
  
    this.getData(this.filterValue);
  }
  
  removeFilter(filter: { displayValue: string; value: Object | string; key: 'minDate' | 'maxDate' | 'providedServices' }) {
    this.isLoading = true;
  
    this.appliedFilters = this.appliedFilters.filter(f => f.value !== filter.value);
    this.badgeCount = this.appliedFilters.length;
  
    if (filter.key === 'minDate') {
      this.filterValue.minDate = '';
      this.filterForm.patchValue({ minDate: '' });
    } else if (filter.key === 'maxDate') {
      this.filterValue.maxDate = '';
      this.filterForm.patchValue({ maxDate: '' });
    } else if (filter.key === 'providedServices') {
      this.filterValue.providedServices = this.filterValue.providedServices.filter((id: number) => id !== filter.value);
      this.filterForm.patchValue({ providedServices: this.filterValue.providedServices.length ? this.filterValue.providedServices : null });
    }
  
    this.getData(this.filterValue);
  }
  
  resetFilter() {
    this.isLoading = true;
    const { appliedFilters, badgeCount } = resetFilterByDateUtil({
      trigger: this.trigger,
      list: [],
      filterForm: this.filterForm,
    });
    this.appliedFilters = appliedFilters;
    this.badgeCount = badgeCount;
    this.filterValue.minDate = '';
    this.filterValue.maxDate = '';
    this.filterValue.providedServices = []; 
    this.filterForm.patchValue({ providedServices: null }); 
    this.getData(this.filterValue);
  }
  
  openDialog(action: 'view' | 'edit' | 'new' | 'delete', obj?: any) {
    this.object.subjectId = this.subjectId;
    this.object.action = action;

    let dialogRef: any;
    if (action === 'delete') {
        this.object.trackingData = obj;
        dialogRef = this.dialog.open(DeleteTrackingComponent, {
          panelClass: 'overlay-panel',
          disableClose: true,
          width: ModalSizeEnum.MINI,
          data: this.object,
        });
    } else if (action === 'new') {
        this.object.trackingData = new Activity();
        dialogRef = this.dialog.open(ServiceProviderManageTrackingComponent, {
          panelClass: 'overlay-panel',
          disableClose: true,
          width: ModalSizeEnum.MINI,
          data: this.object,
        });
    } else {
        this.object.trackingData = obj;
        dialogRef = this.dialog.open(ServiceProviderManageTrackingComponent, {
          panelClass: 'overlay-panel',
          disableClose: true,
          width: ModalSizeEnum.MINI,
          data: this.object,
        });
    }

    dialogRef.afterClosed().subscribe(() => {
      this.isLoading = true;
      this.getData(this.filterValue);
    });
  }
  
  getData(filter: { minDate: any; maxDate: any; providedServices: any }) {
    if (this.page.numberOfElements === 1 && this.page.pageable.pageNumber !== 0) {
      this.page.pageable.pageNumber = this.page.pageable.pageNumber - 1;
    }

    this.getDataForSubject(filter);
  }
  
  /*Metoda poziva akciju stampanja lista pracenja jednog korisnika na osnovu njegovo ID-a*/
  printTrackingList() {
    const body = {
      serviceProviderSubjectId: this.subjectId,
      serviceProviderId: this.serviceProviderId
    };
    this.trackingListService.printServiceProvider(body);
  }

}
